import { notification } from "antd";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { FC, useEffect, useState } from "react";

import { PrimaryText } from "@/components/Typography/Styled";
import { BlockLoader } from "@/components/UI/BlockLoader";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";

dayjs.extend(isBetween);

import {
    getApiError,
    getInvoiceInvoiceWithProductsInfoCollection,
    getInvoicePaymentsListByConsignmentsIds,
    getMarketGetShipmentRangeDate,
    getMarketShipments,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import {
    DateRange,
    InvoiceDetails,
    MarketShipment,
    MarketShipmentPayment,
} from "tm-shared/types";

import { ShipmentAccordions } from "./ShipmentAccordions";
import { ShipmentRangePicker } from "./ShipmentRangePicker";

interface MarketShipmentHistoryProps {
    marketId: string;
    initialDate?: string;
}

interface ShipmentData {
    period: DateRange | null;
    shipments: MarketShipment[];
    invoices: InvoiceDetails[];
    payments: { [consignmentId: string]: MarketShipmentPayment[] };
}

export const MarketShipmentHistory: FC<MarketShipmentHistoryProps> = ({
    marketId,
    initialDate,
}) => {
    const { offlineMode } = useAppSelector((s) => s.offline);
    const dispatch = useAppDispatch();

    const [data, setData] = useState<ShipmentData>({
        period: null,
        shipments: [],
        invoices: [],
        payments: {},
    });

    const [datesConfirmed, setDatesConfirmed] = useState<null | DateRange>(
        null,
    );

    const [loading, setLoading] = useState(false);
    const [loadingShipment, setLoadingShipment] = useState(false);

    useEffect(() => {
        if (offlineMode) return;

        const fetchPeriod = async () => {
            try {
                setLoadingShipment(true);

                const { result: period } = await getMarketGetShipmentRangeDate({
                    marketId,
                });

                setData((prev) => ({ ...prev, period }));

                const initialDayjs = dayjs(initialDate);

                if (
                    initialDate &&
                    initialDayjs.isBetween(
                        period.startDate,
                        period.endDate,
                        "day",
                        "[]",
                    )
                ) {
                    const formatInitialDate = initialDayjs.format(
                        backendDateFormat.date,
                    );

                    setDatesConfirmed({
                        startDate: formatInitialDate,
                        endDate: formatInitialDate,
                    });
                }
            } catch (error) {
                const { displayMessage } = getApiError(error);
                notification.error({
                    message: "Не вдалося отримати період",
                    description: displayMessage,
                });
            } finally {
                setLoadingShipment(false);
            }
        };

        fetchPeriod();
    }, [marketId, dispatch, offlineMode, initialDate]);

    useEffect(() => {
        const fetchHistory = async () => {
            if (!datesConfirmed?.startDate || !datesConfirmed?.endDate) return;

            setLoading(true);
            try {
                const { result: shipments } = await getMarketShipments({
                    marketId,
                    ...datesConfirmed,
                });

                const invoiceIds = shipments
                    .map((s) => s.invoiceId)
                    .filter(Boolean);
                const consignmentsIds = shipments.map((s) => s.consignmentId);

                const [invoicesResult, paymentsResult] = await Promise.all([
                    invoiceIds.length > 0
                        ? getInvoiceInvoiceWithProductsInfoCollection({
                              invoiceIds,
                          })
                        : Promise.resolve(null),
                    consignmentsIds.length > 0
                        ? getInvoicePaymentsListByConsignmentsIds({
                              consignmentsIds,
                          })
                        : Promise.resolve(null),
                ]);

                setData((prev) => ({
                    ...prev,
                    shipments,
                    invoices: invoicesResult?.result ?? [],
                    payments: paymentsResult?.result ?? {},
                }));
            } catch (error) {
                const { displayMessage } = getApiError(error);
                notification.error({
                    message: "Не вдалося завантажити історію",
                    description: displayMessage,
                });
            } finally {
                setLoading(false);
            }
        };

        void fetchHistory();
    }, [marketId, dispatch, datesConfirmed]);

    if (loadingShipment) return <BlockLoader />;

    if (!data.period?.startDate || !data.period?.endDate) {
        return (
            <PrimaryText fontSize={16} textAlign="center">
                За даною ТТ історія продажів не знайдена
            </PrimaryText>
        );
    }

    return (
        <>
            <ShipmentRangePicker
                limits={data.period}
                setDatesConfirmed={setDatesConfirmed}
                disabled={offlineMode || loading}
                datesConfirmed={datesConfirmed}
            />

            {loading ? (
                <BlockLoader />
            ) : (
                <ShipmentAccordions
                    shipments={data.shipments}
                    invoices={data.invoices}
                    payments={data.payments}
                />
            )}
        </>
    );
};
